import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';
import {Match} from '../model/events/Match';
import {SessionService} from './session.service';
import {tap} from 'rxjs/operators';
import {DOMAIN_VALUES_PHASE_MODE, Domains} from '../../assets/domains/domains';
import {MatchParticipant} from '../model/events/MatchParticipant';
import {MatchlistResponse} from '../modules/shared/model/matchlist-response';
import {DateHelper} from '../helper/date.helper';
import {TeamleagueService} from '../modules/backend/event/event-module/event-ergebnisse/service/teamleague.service';
import {DatePipe} from '@angular/common';
import {DatabaseHelper} from '../helper/database.helper';

@Injectable(
  {
    providedIn: 'root'
  }
)
export class MatchService
{

  constructor(private http: HttpClient,
              private sessionService: SessionService,
              private teamleagueService: TeamleagueService,
              private datePipe: DatePipe)
  {

  }

  getMatches(eventId: number, phaseId: number, roundId: number, roundFromIndex: number, matchId: number,
             participantId: number, memberId: number, statusCd: string, withPhases: boolean, withParticipants: boolean,
             withBoards: boolean, withAssociationRules: boolean, showBye: boolean, withEvent: boolean, mainEventId: number,
             onlyWithParticipants: boolean, showFinishedMatches: boolean, gameNr: number, board: string,
             allEvents: boolean, teamId: number, matchday: number, date: Date, playingVenueId: number, filterEventId: number): Observable<MatchlistResponse>
  {
    const filter = new URLSearchParams([
      ['withPhases', withPhases ? '1' : '0'],
      ['withParticipants', withParticipants ? '1' : '0'],
      ['withBoards', withBoards ? '1' : '0'],
      ['withAssociationRules', withAssociationRules ? '1' : '0'],
      ['subrounds', '1']
    ]);

    if (eventId)
      filter.append('eventId', eventId + '');
    if (filterEventId && !eventId)
      filter.append('eventId', filterEventId + '');
    if (allEvents)
      filter.append('allEvents', '1');
    if (phaseId)
      filter.append('phaseId', phaseId + '');
    if (roundId)
      filter.append('roundId', roundId + '');
    if (participantId)
      filter.append('participantId', participantId + '');
    if (memberId)
      filter.append('memberId', memberId + '');
    if (statusCd)
      filter.append('statusCd', statusCd + '');
    if (roundFromIndex)
      filter.append('roundFromIndex', roundFromIndex + '');
    if (matchId && matchId > 0)
      filter.append('matchId', matchId + '');
    if (showBye)
      filter.append('showBye', '1');
    if (mainEventId)
      filter.append('mainEventId', mainEventId + '');
    if (onlyWithParticipants)
      filter.append('onlyWithParticipants', '1');
    if (showFinishedMatches)
      filter.append('showFinishedMatches', '1');
    if (gameNr)
      filter.append('gameNr', gameNr + '');
    if (board)
      filter.append('board', board);
    if (teamId)
      filter.append('teamId', teamId + '');
    if (matchday)
      filter.append('matchday', matchday + '');
    if (playingVenueId)
      filter.append('playingVenueId', playingVenueId + '');
    if (date)
      filter.append('date', DateHelper.toDateApi(date, this.datePipe));

    const url = this.sessionService.baseUrlApi + 'match?' + filter.toString();
    console.log('HTTP GET: ' + url);
    return this.http.get<MatchlistResponse>(url).pipe(
      tap(result => {
        console.log('HTTP GET RESPONSE', result);
        if (result.matches)
        {
          result.matches.forEach((item) =>
          {
            Domains.applyMatchStatus(item);
            if (item.phase && !item.phase.name && item.phase.modeCd)
            {
              item.phase.name = Domains.getDomainValueStr(DOMAIN_VALUES_PHASE_MODE, item.phase.modeCd);
            }

            if (item.participants)
            {
              item.participants.sort((x, y) => x.position - y.position);
            }

            item.beginDate = DateHelper.getDateOrUndefined(item.beginDate);
            item.calledUp = DateHelper.getDateOrUndefined(item.calledUp);
          });
        }

        if (result.boards)
        {
          result.boards.forEach((item) =>
          {
            if (item.match)
            {
              Domains.applyMatchStatus(item.match);
            }
          });
        }

        if (result.match)
        {
          this.teamleagueService.initLeagueMatch(result.match);
        }
      })
    );
  }

  saveMatch(match: Match): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match';
    console.log('HTTP POST: ' + url, match);
    return this.http.post<any>(url, match);
  }
  changeMatchStatus(matchId: number, statusCd: string): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/status/' + statusCd;
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, null);
  }
  updateMatchField(matchId: number, body: any): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/update';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }
  calledUpMatch(matchId: number): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/calledUp';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, null);
  }
  calledUpMatchCancel(matchId: number): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/calledUpCancel';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, null);
  }

  reorderMatch(matchIdFrom: number, matchIdTo: number): Observable<any>
  {
    const body = {
      matchIdFrom, matchIdTo
    };
    const url = this.sessionService.baseUrlApi + 'match/reorder';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }

  putMatchToOtherGroup(matchId: number, groupId: number): Observable<any>
  {
    const body = {
      matchId, groupId
    };
    const url = this.sessionService.baseUrlApi + 'match/reordergroup';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }

  getMatch(matchId: number): Observable<Match>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId;
    console.log('HTTP GET: ' + url);
    return this.http.get<Match>(url).pipe(
      tap(result => {
        console.log('HTTP GET RESPONSE', result);
        Domains.applyMatchStatus(result);
      })
    );
  }
  getMatchDatabase(databaseId: number, matchId: number): Observable<Match>
  {
    const url = DatabaseHelper.getApiUrl(databaseId) + 'match/' + matchId;
    console.log('HTTP GET: ' + url);
    return this.http.get<Match>(url).pipe(
      tap(result => {
        console.log('HTTP GET RESPONSE', result);
        Domains.applyMatchStatus(result);
      })
    );
  }

  getMatchByMatchApproval(matchApprovalId: number): Observable<Match>
  {
    const url = this.sessionService.baseUrlApi + 'match/approval/' + matchApprovalId;
    console.log('HTTP GET: ' + url);
    return this.http.get<Match>(url).pipe(
      tap(result => {
        console.log('HTTP GET RESPONSE', result);
        Domains.applyMatchStatus(result);
      })
    );
  }

  getBoards(eventId: number): Observable<any[]>
  {
    const url = this.sessionService.baseUrlApi + 'match/boards/' + eventId;
    console.log('HTTP GET: ' + url);
    return this.http.get<any[]>(url);
  }

  deleteMatch(matchId: number): Observable<Match>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId;
    console.log('HTTP DELETE: ' + url);
    return this.http.delete<Match>(url);
  }

  setMatchParticipantsResult(matchId: number, participants: MatchParticipant[]): Observable<any>
  {
    const body = {
      matchParticipants: participants
    };
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/participants';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }

  callOutMatch(matchId: number, body: any): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/callout';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }
  callOutPresent(matchId: number, participantId: number): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/callout/participant/' + participantId;
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, undefined);
  }
  getCallOutHistory(matchId: number): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/callout';
    console.log('HTTP GET: ' + url);
    return this.http.get<any>(url).pipe(
      tap(result => {
        result.forEach((item) =>
        {
          if (item.participantHome && item.participantHome.presentSince)
            item.buttonHomeDisabled = true;
          if (item.participantGuest && item.participantGuest.presentSince)
            item.buttonGuestDisabled = true;
          if (item.writer && item.writer.presentSince)
            item.buttonWriterDisabled = true;

          item.participantHomeRecallValue = !!item.participantHomeRecall;
          item.participantGuestRecallValue = !!item.participantGuestRecall;
          item.writerRecallValue = !!item.writerRecall;
          item.participantHomeRecallDate = item.participantHomeRecall ? this.datePipe.transform(item.participantHomeRecall, 'dd.MM.yyyy HH:mm:ss') : '';
          item.participantGuestRecallDate = item.participantGuestRecall ? this.datePipe.transform(item.participantGuestRecall, 'dd.MM.yyyy HH:mm:ss') : '';
          item.writerRecallDate = item.writerRecall ? this.datePipe.transform(item.writerRecall, 'dd.MM.yyyy HH:mm:ss') : '';
        });
      })
    );
  }
  recallParticipant(matchId: number, participantId: number): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/callout/participant/' + participantId +
      '/recall';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, undefined);
  }
  getCallOutParticipantHistory(matchId: number, participantIds: number[]): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/callout/participant';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, participantIds);
  }
  deleteCallOut(matchId: number, callOutId: number): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/callout/' + callOutId;
    console.log('HTTP DELETE: ' + url);
    return this.http.delete<any>(url);
  }

  getLogbook(eventId: number, mainEventId: number): Observable<Match[]>
  {
    const filter = new URLSearchParams([]);
    if (eventId)
      filter.append('eventId', eventId + '');
    if (mainEventId)
      filter.append('mainEventId', mainEventId + '');

    const url = this.sessionService.baseUrlApi + 'match/logbook?' + filter.toString();
    console.log('HTTP GET: ' + url);
    return this.http.get<any[]>(url);
  }

  getRescheduleMatch(matchId: number, matchRescheduleId: number): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + (matchId > 0 ? (matchId + '/rescheduleInfo') :
      ('rescheduleInfo/' + matchRescheduleId));
    console.log('HTTP GET: ' + url);
    return this.http.get<any>(url);
  }

  requestRescheduleMatch(matchId: number, body: any): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/reschedule/request';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }

  rescheduleMatchChangeStatus(matchId: number, body: any): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/reschedule/status';
    console.log('HTTP POST: ' + url, body);
    return this.http.post<any>(url, body);
  }

  public getMatchPin(matchId: number): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/pin';
    console.log('HTTP GET: ' + url);
    return this.http.get<any>(url);
  }
  public createMatchPin(matchId: number): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/pin';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, null);
  }
  public deleteMatchPin(matchId: number): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + matchId + '/pin';
    console.log('HTTP DELETE: ' + url);
    return this.http.delete<any>(url);
  }

  public assumeMatchdayDateToMatches(roundId: number): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/round/' + roundId + '/assumeDate';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, null);
  }

  public assumeMatchreportBoards(parentMatchId: number): Observable<any>
  {
    const url = this.sessionService.baseUrlApi + 'match/' + parentMatchId + '/assumeBoards';
    console.log('HTTP POST: ' + url);
    return this.http.post<any>(url, undefined);
  }

}
